/**
 * Region capture (架构方案 §5.8). One session returns a PNG, a clipboard copy,
 * or a cancel. The platform does not know about chats; callers decide what a
 * confirm means.
 */

/** Command id. Built-in, handled in the extension host (not a module). */
export const CAPTURE_REGION_COMMAND = 'workbench.captureRegion';

export interface CaptureRegionCall {
  /**
   * Hide this app's visible windows before grabbing pixels. Default leaves
   * them visible. Pass `true` to hide them for the grab.
   */
  hideHostWindows?: boolean;
  /**
   * `return` hands the PNG to the caller, who decides what to do with it.
   * `clipboard` writes the PNG on confirm and resolves as `copy`.
   * Omitted means `clipboard`, so a command-palette run does not drop the image.
   */
  purpose?: 'return' | 'clipboard';
}

export interface CaptureRegionConfirm {
  action: 'confirm';
  png: Uint8Array;
}

export interface CaptureRegionCopied {
  action: 'copy';
}

export interface CaptureRegionCancelled {
  action: 'cancel';
}

export type CaptureRegionResult =
  | CaptureRegionConfirm
  | CaptureRegionCopied
  | CaptureRegionCancelled;

/** Read the one optional argument object a caller passed to the command. */
export function readCaptureRegionCall(
  args: readonly unknown[],
): CaptureRegionCall {
  const first = args[0];
  if (!first || typeof first !== 'object') {
    return {};
  }
  const record = first as CaptureRegionCall;
  return {
    ...(record.hideHostWindows === true ? { hideHostWindows: true } : {}),
    ...(record.purpose === 'return' || record.purpose === 'clipboard'
      ? { purpose: record.purpose }
      : {}),
  };
}

/**
 * Editor finish payload from the capture window. `null` is a broken payload:
 * the session must reject, not look like the user cancelled.
 */
export function readCaptureFinish(value: unknown): CaptureRegionResult | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const record = value as { action?: unknown; png?: unknown };
  if (record.action === 'copy' || record.action === 'cancel') {
    return { action: record.action };
  }
  if (record.action === 'confirm' && record.png instanceof Uint8Array) {
    return { action: 'confirm', png: record.png };
  }
  return null;
}

/**
 * CSS pixels that put each bitmap pixel on one device pixel.
 * `scaleFactor` is the scale the main process used to size the capture window.
 */
export function bitmapContentSize(
  pixelWidth: number,
  pixelHeight: number,
  scaleFactor: number,
): { width: number; height: number } {
  if (!(pixelWidth > 0) || !(pixelHeight > 0)) {
    throw new Error('capture bitmap has no pixels');
  }
  if (!(scaleFactor > 0)) {
    throw new Error('capture scale factor is invalid');
  }
  const width = Math.round(pixelWidth / scaleFactor);
  const height = Math.round(pixelHeight / scaleFactor);
  if (width <= 0 || height <= 0) {
    throw new Error('capture bitmap rounds to an empty client area');
  }
  return { width, height };
}

/**
 * Black the editor composites over the frozen frame until a selection exists.
 * The present check applies this same veil: the compositor frame is that
 * darkened image, not the raw bitmap.
 */
export const CAPTURE_VEIL_ALPHA = 0.45;

/** Confirm writes the clipboard unless the caller asked for the bytes back. */
export function capturePurpose(
  call: CaptureRegionCall,
): 'return' | 'clipboard' {
  return call.purpose === 'return' ? 'return' : 'clipboard';
}
